/**
 * PauseMenu — In-game pause overlay for The Cats of Chronos.
 *
 * Features:
 * - Triggered by Escape key or gamepad Menu/Options during gameplay
 * - Semi-transparent dark overlay
 * - Options: Resume, Restart Level, Quit to Menu
 * - Pauses the scene render loop while displayed
 * - Gothic steampunk styling consistent with title/level select
 */
import { Scene } from "@babylonjs/core/scene";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { Slider } from "@babylonjs/gui/2D/controls/sliders/slider";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { COLORS } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { configureResponsiveFullscreenUI } from "../ui/guiScaling";
import { AccessibilitySystem } from "../systems/AccessibilitySystem";
import { scaledFont, resolveContrastStyle } from "../ui/menuFonts";
import { isAnyGamepadButtonPressed } from "../ui/ControlGlyphs";
import { SaveSystem, VolumePrefs } from "../systems/SaveSystem";
import { resolveFocusTarget } from "../ui/menuFocus";

/** A focusable control is either a Button or a Slider. */
type FocusableControl = Button | Slider;

/**
 * Exported factory for the audio slider onChange wiring.
 * Separated from GUI so unit tests can exercise it without Babylon GUI.
 *
 * @param channel  - which channel this slider controls ("master"|"sfx"|"music")
 * @param current  - shared mutable volume object (mutated in place on each call)
 * @param save     - function to persist volumes (typically SaveSystem#saveVolumes)
 * @returns a function that accepts the new value, persists, and emits VOLUME_CHANGED
 */
export function buildAudioOnChange(
  channel: keyof VolumePrefs,
  current: VolumePrefs,
  save: (v: VolumePrefs) => void,
): (value: number) => void {
  return (value: number) => {
    current[channel] = value;
    const merged: VolumePrefs = { master: current.master, sfx: current.sfx, music: current.music };
    save(merged);
    gameEvents.emit(GameEvents.VOLUME_CHANGED, merged);
  };
}

export class PauseMenu {
  private static readonly CUTSCENE_ESCAPE_GRACE_MS = 250;
  /** Step size for slider keyboard/gamepad adjustment (10% of range). */
  private static readonly SLIDER_STEP = 0.1;

  private scene: Scene;
  private gui: AdvancedDynamicTexture | null = null;
  private disposeGuiScaling: (() => void) | null = null;
  private overlay: Rectangle | null = null;
  private isPaused = false;
  private keyHandler: ((evt: KeyboardEvent) => void) | null = null;
  private gamepadPauseObserver: Observer<Scene> | null = null;
  private gamepadPauseButtonDown = false;
  private cutsceneActive = false;
  private lastCutsceneEndedAt = -Infinity;
  private unsubCutsceneStarted: (() => void) | null = null;
  private unsubCutsceneEnded: (() => void) | null = null;
  private unsubA11y: (() => void) | null = null;
  private trackedButtons: Button[] = [];

  // --- Focus navigation state ---
  /** Which panel is currently active: "main" | "a11y" | "audio" | "rebind" */
  private activePanel: "main" | "a11y" | "audio" | "rebind" = "main";
  /** Ordered list of focusable controls in the active panel. */
  private focusable: FocusableControl[] = [];
  /** Index of the currently focused control. */
  private focusIndex = 0;
  /** Background color for a focused button (hover tint). */
  private static readonly FOCUS_BG = "rgba(0, 255, 136, 0.15)";
  private static readonly FOCUS_COLOR = COLORS.BIOLUMINESCENT_GREEN;
  /** Default button background used in createMenuButton. */
  private static readonly DEFAULT_BTN_BG = "rgba(42, 31, 61, 0.9)";
  private static readonly DEFAULT_BTN_COLOR = "#ffffff";

  // Gamepad edge-detection state for nav buttons
  private gpDpadDownDown = false;
  private gpDpadUpDown = false;
  private gpBtnADown = false;
  private gpBtnBDown = false;

  constructor(scene: Scene) {
    this.scene = scene;
    // If the opening cutscene started before PauseMenu was attached,
    // we missed the CUTSCENE_STARTED event — read current state from
    // the scene-stashed cutscene system so Escape routes to skip, not
    // pause, during that window.
    const stashed = (scene as unknown as { __cutsceneSystem?: { isActive: boolean } }).__cutsceneSystem;
    if (stashed?.isActive) {
      this.cutsceneActive = true;
    }
    this.setupInput();
  }

  /** Whether the game is currently paused. */
  get paused(): boolean {
    return this.isPaused;
  }

  /**
   * Handle a navigation key press. Extracted for testability — call this
   * directly in unit tests instead of dispatching DOM KeyboardEvents (which
   * are unavailable in the node test environment).
   *
   * Returns true if the key was consumed (caller should preventDefault/stopPropagation).
   */
  handleNavKey(key: string): boolean {
    if (key === "Escape") {
      // Cutscenes own Escape for skipping; don't steal it from them.
      if (this.cutsceneActive) return false;
      if (performance.now() - this.lastCutsceneEndedAt < PauseMenu.CUTSCENE_ESCAPE_GRACE_MS) {
        return false;
      }
      if (this.isPaused) {
        // If a sub-overlay is open, close it back to main panel instead of resuming.
        if (this.activePanel !== "main") {
          this.closeSubOverlay();
          return true;
        }
        this.resume();
      } else {
        this.pause();
      }
      return true;
    }

    // Navigation keys only act while paused.
    if (!this.isPaused) return false;

    switch (key) {
      case "ArrowDown":
        this.moveFocus(1);
        return true;
      case "ArrowUp":
        this.moveFocus(-1);
        return true;
      case "ArrowRight":
        this.adjustSlider(PauseMenu.SLIDER_STEP);
        return true;
      case "ArrowLeft":
        this.adjustSlider(-PauseMenu.SLIDER_STEP);
        return true;
      case "Enter":
      case " ":
        this.activateFocused();
        return true;
    }
    return false;
  }

  private setupInput(): void {
    this.keyHandler = (evt: KeyboardEvent) => {
      const consumed = this.handleNavKey(evt.key);
      if (consumed) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    };
    window.addEventListener("keydown", this.keyHandler);
    this.gamepadPauseObserver = this.scene.onBeforeRenderObservable.add(() => {
      this.pollGamepadPauseInput();
    });

    this.unsubCutsceneStarted = gameEvents.on(GameEvents.CUTSCENE_STARTED, () => {
      this.cutsceneActive = true;
    });
    this.unsubCutsceneEnded = gameEvents.on(GameEvents.CUTSCENE_ENDED, () => {
      this.cutsceneActive = false;
      this.lastCutsceneEndedAt = performance.now();
    });
    this.unsubA11y = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
      this.applyAccessibilityTuning();
    });
  }

  private applyAccessibilityTuning(): void {
    const a11y = AccessibilitySystem.getInstance();
    for (const btn of this.trackedButtons) {
      if (!(btn as any).isDisposed) {
        btn.fontSize = scaledFont(20);
      }
    }
    if (this.overlay) {
      const style = resolveContrastStyle(a11y.state.highContrastUI);
      this.overlay.background = style.background;
      this.overlay.thickness = style.thickness;
    }
  }

  private pollGamepadPauseInput(): void {
    const btn9 = isAnyGamepadButtonPressed([9]);
    if (btn9 && !this.gamepadPauseButtonDown) {
      if (!this.cutsceneActive && performance.now() - this.lastCutsceneEndedAt >= PauseMenu.CUTSCENE_ESCAPE_GRACE_MS) {
        if (this.isPaused) {
          this.resume();
        } else {
          this.pause();
        }
      }
    }
    this.gamepadPauseButtonDown = btn9;

    // Navigation is only relevant while paused.
    if (!this.isPaused) return;

    // DPad down (button 13) — move focus forward
    const dpadDown = isAnyGamepadButtonPressed([13]);
    if (dpadDown && !this.gpDpadDownDown) {
      this.moveFocus(1);
    }
    this.gpDpadDownDown = dpadDown;

    // DPad up (button 12) — move focus backward
    const dpadUp = isAnyGamepadButtonPressed([12]);
    if (dpadUp && !this.gpDpadUpDown) {
      this.moveFocus(-1);
    }
    this.gpDpadUpDown = dpadUp;

    // Button 0 (A) — activate focused control
    const btnA = isAnyGamepadButtonPressed([0]);
    if (btnA && !this.gpBtnADown) {
      this.activateFocused();
    }
    this.gpBtnADown = btnA;

    // Button 1 (B) — back/Escape behavior
    const btnB = isAnyGamepadButtonPressed([1]);
    if (btnB && !this.gpBtnBDown) {
      if (this.activePanel !== "main") {
        this.closeSubOverlay();
      } else {
        this.resume();
      }
    }
    this.gpBtnBDown = btnB;
  }

  // ---------------------------------------------------------------------------
  // Focus navigation helpers
  // ---------------------------------------------------------------------------

  /**
   * Rebuild the focusable list for the given panel and reset focusIndex to 0.
   * Called by each show*Overlay method and createOverlay.
   */
  private setFocusable(controls: FocusableControl[], panel: "main" | "a11y" | "audio" | "rebind"): void {
    // Remove highlight from old focused item (if any)
    this.clearFocusHighlight();
    this.focusable = controls;
    this.focusIndex = 0;
    this.activePanel = panel;
    // Apply highlight to first item
    this.applyFocusHighlight();
  }

  /** Apply hover-tint to the currently focused control. */
  private applyFocusHighlight(): void {
    if (this.focusable.length === 0) return;
    const ctrl = this.focusable[this.focusIndex] as any;
    // Duck-type: buttons have background + color + no minimum/maximum (unlike sliders)
    if (ctrl.background !== undefined && ctrl.minimum === undefined) {
      ctrl.background = PauseMenu.FOCUS_BG;
      ctrl.color = PauseMenu.FOCUS_COLOR;
    }
    // Sliders don't change background on focus — their thumb color signals focus.
    // (No-op for now; future enhancement: add a glow border.)
  }

  /** Remove hover-tint from the currently focused control. */
  private clearFocusHighlight(): void {
    if (this.focusable.length === 0) return;
    const ctrl = this.focusable[this.focusIndex] as any;
    // Duck-type: buttons have background + color + no minimum/maximum (unlike sliders)
    if (ctrl.background !== undefined && ctrl.minimum === undefined) {
      // Restore to panel-appropriate default.
      // Main panel buttons use "#ffffff" text; sub-overlay buttons use BRASS or "#ffffff".
      // We store the original colors so we can restore them exactly.
      ctrl.background = ctrl.__origBg ?? PauseMenu.DEFAULT_BTN_BG;
      ctrl.color = ctrl.__origColor ?? PauseMenu.DEFAULT_BTN_COLOR;
    }
  }

  /**
   * Move focus by `dir` steps, skipping disabled items.
   * All items in focusable[] are always enabled (no disabled mask needed currently).
   */
  private moveFocus(dir: 1 | -1): void {
    if (this.focusable.length === 0) return;
    this.clearFocusHighlight();
    const enabledMask = this.focusable.map(() => true);
    this.focusIndex = resolveFocusTarget(this.focusIndex, dir, enabledMask);
    this.applyFocusHighlight();
  }

  /** Activate (click) the currently focused control if it has a click observable. */
  private activateFocused(): void {
    if (this.focusable.length === 0) return;
    const ctrl = this.focusable[this.focusIndex];
    // Duck-type: any control with onPointerClickObservable is activatable.
    const asBtn = ctrl as unknown as { onPointerClickObservable?: { notifyObservers: (e: unknown) => void } };
    if (asBtn.onPointerClickObservable) {
      asBtn.onPointerClickObservable.notifyObservers({} as any);
    }
  }

  /** Adjust slider value by `delta` if the focused control is a Slider. */
  private adjustSlider(delta: number): void {
    if (this.focusable.length === 0) return;
    const ctrl = this.focusable[this.focusIndex];
    // Duck-type: sliders have minimum/maximum/value properties.
    const asSlider = ctrl as unknown as { minimum?: number; maximum?: number; value?: number };
    if (asSlider.minimum !== undefined && asSlider.maximum !== undefined && asSlider.value !== undefined) {
      asSlider.value = Math.min(asSlider.maximum, Math.max(asSlider.minimum, asSlider.value + delta));
    }
  }

  /**
   * Close the active sub-overlay and return to the main panel.
   * Finds the sub-panel container by name and disposes it, restores main panel visibility.
   */
  private closeSubOverlay(): void {
    if (!this.overlay) return;
    const panelName =
      this.activePanel === "a11y" ? "a11yPanel" :
      this.activePanel === "audio" ? "audioPanel" :
      this.activePanel === "rebind" ? "rebindPanel" : null;

    if (panelName) {
      const container = this.overlay.getChildByName(panelName);
      if (container) {
        container.dispose();
      }
    }

    // Restore main panel visibility
    const pausePanel = this.overlay.getChildByName("pausePanel") || this.overlay.children[0];
    if (pausePanel) {
      pausePanel.isVisible = true;
    }

    // Rebuild focusable for main panel
    this.rebuildMainFocusable();
  }

  /**
   * Collect all Button children of the main pausePanel into the focusable list.
   * Called after returning from a sub-overlay.
   */
  private rebuildMainFocusable(): void {
    if (!this.overlay) return;
    const pausePanel = this.overlay.getChildByName("pausePanel") || this.overlay.children[0];
    if (!pausePanel) return;
    const container = pausePanel as unknown as { children: Control[] };
    const buttons: Button[] = [];
    for (const child of container.children) {
      if (child instanceof Button) {
        buttons.push(child);
      }
    }
    this.setFocusable(buttons, "main");
  }

  /** Show the pause menu and freeze the scene. */
  pause(): void {
    if (this.isPaused) return;
    this.isPaused = true;

    // Freeze active animations and physics by disabling the before-render loop
    this.scene.animationsEnabled = false;
    this.scene.physicsEnabled = false;

    this.createOverlay();
    gameEvents.emit(GameEvents.GAME_PAUSED);
  }

  /** Hide the pause menu and resume the scene. */
  resume(): void {
    if (!this.isPaused) return;
    this.isPaused = false;

    this.scene.animationsEnabled = true;
    this.scene.physicsEnabled = true;
    this.destroyOverlay();
    gameEvents.emit(GameEvents.GAME_RESUMED);
  }

  private createOverlay(): void {
    this.gui = AdvancedDynamicTexture.CreateFullscreenUI("pauseUI", true, this.scene);
    this.disposeGuiScaling = configureResponsiveFullscreenUI(this.gui, this.scene);

    // Dark semi-transparent backdrop
    this.overlay = new Rectangle("pauseBackdrop");
    this.overlay.width = "100%";
    this.overlay.height = "100%";
    this.overlay.thickness = 0;
    this.overlay.background = "rgba(5, 2, 15, 0.82)";
    this.overlay.isPointerBlocker = true;
    this.gui.addControl(this.overlay);

    // Content panel
    const panel = new StackPanel("pausePanel");
    panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    panel.width = "320px";
    this.overlay.addControl(panel);

    // Title
    const title = new TextBlock("pauseTitle");
    title.text = "Paused";
    title.color = COLORS.BIOLUMINESCENT_GREEN;
    title.fontSize = 38;
    title.fontFamily = "Georgia, 'Times New Roman', serif";
    title.fontWeight = "bold";
    title.height = "60px";
    title.shadowColor = COLORS.DEEP_PURPLE;
    title.shadowBlur = 6;
    panel.addControl(title);

    // Divider
    const divider = new Rectangle("pauseDivider");
    divider.width = "200px";
    divider.height = "2px";
    divider.thickness = 0;
    divider.background = COLORS.BRASS;
    divider.alpha = 0.5;
    panel.addControl(divider);

    // Spacer
    const spacer = new Rectangle("spacer");
    spacer.height = "30px";
    spacer.thickness = 0;
    spacer.background = "transparent";
    panel.addControl(spacer);

    // Resume button
    const resumeBtn = this.createMenuButton("resumeBtn", "Resume", () => {
      this.resume();
    });
    panel.addControl(resumeBtn);

    // Restart button
    const restartBtn = this.createMenuButton("restartBtn", "Restart Level", () => {
      this.resume();
      gameEvents.emit(GameEvents.PAUSE_RESTART_LEVEL);
    });
    panel.addControl(restartBtn);

    // Restart from Checkpoint button
    const restartCheckpointBtn = this.createMenuButton(
      "restartCheckpointBtn",
      "Restart from Checkpoint",
      () => {
        this.resume();
        gameEvents.emit(GameEvents.PAUSE_RESTART_FROM_CHECKPOINT);
      },
    );
    panel.addControl(restartCheckpointBtn);

    // Controls Rebindings button
    const rebindBtn = this.createMenuButton("rebindBtn", "Control Rebindings", () => {
      this.showRebindingsOverlay();
    });
    panel.addControl(rebindBtn);

    // Accessibility Options button
    const a11yBtn = this.createMenuButton("a11yBtn", "Accessibility Options", () => {
      this.showAccessibilityOverlay();
    });
    panel.addControl(a11yBtn);

    // Audio Settings button
    const audioBtn = this.createMenuButton("audioBtn", "Audio Settings", () => {
      this.showAudioOverlay();
    });
    panel.addControl(audioBtn);

    // Quit to menu button
    const quitBtn = this.createMenuButton("quitBtn", "Quit to Menu", () => {
      this.resume();
      gameEvents.emit(GameEvents.PAUSE_QUIT_TO_MENU);
    });
    panel.addControl(quitBtn);

    // Build focusable list for the main panel
    this.setFocusable([resumeBtn, restartBtn, restartCheckpointBtn, rebindBtn, a11yBtn, audioBtn, quitBtn], "main");
  }

  private createMenuButton(name: string, label: string, onClick: () => void): Button {
    const btn = Button.CreateSimpleButton(name, label);
    btn.width = "260px";
    btn.height = "50px";
    btn.color = "#ffffff";
    btn.fontSize = scaledFont(20);
    btn.fontFamily = "Georgia, 'Times New Roman', serif";
    btn.cornerRadius = 8;
    btn.thickness = 1.5;
    btn.background = "rgba(42, 31, 61, 0.9)";
    btn.paddingTop = "8px";
    btn.paddingBottom = "8px";

    // Stash original colors for focus highlight restoration
    (btn as any).__origBg = "rgba(42, 31, 61, 0.9)";
    (btn as any).__origColor = "#ffffff";

    btn.onPointerClickObservable.add(onClick);
    btn.onPointerEnterObservable.add(() => {
      btn.background = "rgba(0, 255, 136, 0.15)";
      btn.color = COLORS.BIOLUMINESCENT_GREEN;
    });
    btn.onPointerOutObservable.add(() => {
      btn.background = "rgba(42, 31, 61, 0.9)";
      btn.color = "#ffffff";
    });

    this.trackedButtons.push(btn);
    return btn;
  }

  private destroyOverlay(): void {
    // NOTE: unsubA11y is a LIFETIME subscription — it must survive pause/resume
    // cycles so ACCESSIBILITY_CHANGED keeps updating the overlay on re-pause.
    // It is only removed in dispose(). Do NOT call unsubA11y here.
    this.trackedButtons = [];
    this.disposeGuiScaling?.();
    this.disposeGuiScaling = null;
    if (this.gui) {
      this.gui.dispose();
      this.gui = null;
    }
    this.overlay = null;
    // Reset focus state
    this.focusable = [];
    this.focusIndex = 0;
    this.activePanel = "main";
  }

  private showRebindingsOverlay(): void {
    if (!this.overlay) return;

    // Find our main pause menu vertical panel and hide it
    const pausePanel = this.overlay.getChildByName("pausePanel") || this.overlay.children[0];
    if (pausePanel) {
      pausePanel.isVisible = false;
    }
    // Clear focus highlight before switching panels
    this.clearFocusHighlight();

    // Create a new beautiful scroll-like card for rebindings
    const rebindContainer = new StackPanel("rebindPanel");
    rebindContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    rebindContainer.width = "480px";
    this.overlay.addControl(rebindContainer);

    // Title
    const title = new TextBlock("rebindTitle");
    title.text = "Control Rebindings";
    title.color = COLORS.BIOLUMINESCENT_GREEN;
    title.fontSize = 28;
    title.fontFamily = "Georgia, 'Times New Roman', serif";
    title.fontWeight = "bold";
    title.height = "44px";
    rebindContainer.addControl(title);

    const subtitle = new TextBlock("rebindSubtitle");
    subtitle.text = "Click any binding to rekey. Press H for hints.";
    subtitle.color = "#c5b089";
    subtitle.fontSize = 12;
    subtitle.fontFamily = "Segoe UI, Arial, sans-serif";
    subtitle.height = "20px";
    rebindContainer.addControl(subtitle);

    // Divider
    const divider = new Rectangle("rebindDivider");
    divider.width = "360px";
    divider.height = "2px";
    divider.thickness = 0;
    divider.background = COLORS.BRASS;
    divider.alpha = 0.4;
    divider.paddingBottom = "8px";
    rebindContainer.addControl(divider);

    // Access input manager
    const inputManager = (this.scene.metadata as any)?.inputManager;
    const currentBindings = inputManager ? { ...inputManager.bindings } : null;

    if (!currentBindings) {
      const errText = new TextBlock("errText", "Input bindings not available.");
      errText.color = "#ff8844";
      errText.height = "40px";
      rebindContainer.addControl(errText);
    } else {
      const actionList = [
        { label: "P1 Move Left", category: "keyboard", key: "p1Left" },
        { label: "P1 Move Right", category: "keyboard", key: "p1Right" },
        { label: "P1 Jump", category: "keyboard", key: "p1Jump" },
        { label: "P1 Ability (Purr)", category: "keyboard", key: "p1Ability" },
        { label: "P2 Move Left", category: "keyboard", key: "p2Left" },
        { label: "P2 Move Right", category: "keyboard", key: "p2Right" },
        { label: "P2 Jump", category: "keyboard", key: "p2Jump" },
        { label: "P2 Ability", category: "keyboard", key: "p2Ability" },
      ];

      for (const action of actionList) {
        const row = new Rectangle(`row_${action.key}`);
        row.width = "420px";
        row.height = "34px";
        row.thickness = 0;
        row.paddingBottom = "4px";
        rebindContainer.addControl(row);

        const lbl = new TextBlock(`lbl_${action.key}`, action.label);
        lbl.color = "#f5eedb";
        lbl.fontSize = 14;
        lbl.fontFamily = "Segoe UI, Arial, sans-serif";
        // Constrain the label to the left half of the 420px row and
        // left-align its TEXT. Without an explicit width the TextBlock
        // filled the whole row and (default) center-aligned its text,
        // landing it on top of the right-aligned value chip.
        lbl.width = "230px";
        lbl.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        lbl.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        lbl.left = "10px";
        row.addControl(lbl);

        const currentVal = (currentBindings as any)[action.category][action.key];
        const btnText = currentVal === " " ? "Space" : currentVal.toUpperCase();
        const btn = Button.CreateSimpleButton(`btn_${action.key}`, btnText);
        btn.width = "160px";
        btn.height = "26px";
        btn.color = COLORS.BRASS;
        btn.fontSize = 12;
        btn.fontFamily = "Segoe UI, Arial, sans-serif";
        btn.cornerRadius = 4;
        btn.thickness = 1;
        btn.background = "rgba(42, 31, 61, 0.8)";
        btn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        btn.left = "-10px";
        row.addControl(btn);

        btn.onPointerClickObservable.add(() => {
          btn.textBlock!.text = "Press any key...";
          btn.color = COLORS.BIOLUMINESCENT_GREEN;
          btn.background = "rgba(0, 255, 136, 0.1)";

          const onKey = (e: KeyboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.removeEventListener("keydown", onKey);

            // Rebind and save
            const newKey = e.key;
            (currentBindings as any)[action.category][action.key] = newKey;
            if (inputManager) {
              inputManager.applyBindings(currentBindings);
            }
            
            // Update button text
            btn.textBlock!.text = newKey === " " ? "Space" : newKey.toUpperCase();
            btn.color = COLORS.BRASS;
            btn.background = "rgba(42, 31, 61, 0.8)";
            
            // Re-hydrate prompts
            gameEvents.emit(GameEvents.HUD_GLYPHS_CHANGED, { controllerActive: inputManager.isGamepadActive });
          };
          window.addEventListener("keydown", onKey);
        });
      }
    }

    const buttonRow = new StackPanel("buttonRow");
    buttonRow.isVertical = false;
    buttonRow.height = "50px";
    buttonRow.paddingTop = "10px";
    buttonRow.spacing = 10;
    rebindContainer.addControl(buttonRow);

    // Reset defaults button
    const resetBtn = Button.CreateSimpleButton("resetBtn", "Reset Defaults");
    resetBtn.width = "180px";
    resetBtn.height = "36px";
    resetBtn.color = "#ffffff";
    resetBtn.fontSize = 14;
    resetBtn.fontFamily = "Segoe UI, Arial, sans-serif";
    resetBtn.cornerRadius = 6;
    resetBtn.thickness = 1;
    resetBtn.background = "rgba(60, 30, 30, 0.8)";
    buttonRow.addControl(resetBtn);

    resetBtn.onPointerClickObservable.add(() => {
      if (inputManager) {
        const DEFAULT_BINDINGS = {
          keyboard: {
            p1Left: "a",
            p1Right: "d",
            p1Jump: " ",
            p1Ability: "f",
            p2Left: "arrowleft",
            p2Right: "arrowright",
            p2Jump: "enter",
            p2Ability: "/",
          },
          gamepad: {
            jump: 0,
            ability: 1,
          }
        };
        inputManager.applyBindings(DEFAULT_BINDINGS);
        
        // Close and re-open to refresh all fields
        rebindContainer.dispose();
        this.showRebindingsOverlay();
      }
    });

    // Back button
    const backBtn = Button.CreateSimpleButton("backBtn", "Back");
    backBtn.width = "140px";
    backBtn.height = "36px";
    backBtn.color = "#ffffff";
    backBtn.fontSize = 14;
    backBtn.fontFamily = "Segoe UI, Arial, sans-serif";
    backBtn.cornerRadius = 6;
    backBtn.thickness = 1;
    backBtn.background = "rgba(42, 31, 61, 0.9)";
    buttonRow.addControl(backBtn);

    backBtn.onPointerClickObservable.add(() => {
      rebindContainer.dispose();
      if (pausePanel) {
        pausePanel.isVisible = true;
      }
      this.rebuildMainFocusable();
    });

    // Build focusable list for the rebind panel: key-binding buttons + reset + back
    const rebindFocusable: Button[] = [];
    for (const child of rebindContainer.children) {
      if (child instanceof Rectangle) {
        for (const rowChild of child.children) {
          if (rowChild instanceof Button) {
            (rowChild as any).__origBg = "rgba(42, 31, 61, 0.8)";
            (rowChild as any).__origColor = COLORS.BRASS;
            rebindFocusable.push(rowChild);
          }
        }
      } else if (child instanceof StackPanel) {
        // buttonRow containing reset + back
        for (const rowChild of child.children) {
          if (rowChild instanceof Button) {
            (rowChild as any).__origBg = rowChild.name === "resetBtn" ? "rgba(60, 30, 30, 0.8)" : "rgba(42, 31, 61, 0.9)";
            (rowChild as any).__origColor = "#ffffff";
            rebindFocusable.push(rowChild);
          }
        }
      }
    }
    this.setFocusable(rebindFocusable, "rebind");
  }

  private showAccessibilityOverlay(): void {
    if (!this.overlay) return;

    // Find our main pause menu vertical panel and hide it
    const pausePanel = this.overlay.getChildByName("pausePanel") || this.overlay.children[0];
    if (pausePanel) {
      pausePanel.isVisible = false;
    }
    // Clear focus highlight before switching panels
    this.clearFocusHighlight();

    // Create a new beautiful scroll-like card for accessibility
    const a11yContainer = new StackPanel("a11yPanel");
    a11yContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    a11yContainer.width = "480px";
    this.overlay.addControl(a11yContainer);

    // Title
    const title = new TextBlock("a11yTitle");
    title.text = "Accessibility Options";
    title.color = COLORS.BIOLUMINESCENT_GREEN;
    title.fontSize = 28;
    title.fontFamily = "Georgia, 'Times New Roman', serif";
    title.fontWeight = "bold";
    title.height = "44px";
    a11yContainer.addControl(title);

    const subtitle = new TextBlock("a11ySubtitle");
    subtitle.text = "Fine-tune visuals, timing, and gameplay feel.";
    subtitle.color = "#c5b089";
    subtitle.fontSize = 12;
    subtitle.fontFamily = "Segoe UI, Arial, sans-serif";
    subtitle.height = "20px";
    a11yContainer.addControl(subtitle);

    // Divider
    const divider = new Rectangle("a11yDivider");
    divider.width = "360px";
    divider.height = "2px";
    divider.thickness = 0;
    divider.background = COLORS.BRASS;
    divider.alpha = 0.4;
    divider.paddingBottom = "8px";
    a11yContainer.addControl(divider);

    const a11y = AccessibilitySystem.getInstance();

    // Helper to create a toggle/cycle row
    const createOptionRow = (
      labelText: string,
      initialValue: string,
      onCycle: (btn: Button) => string
    ) => {
      const row = new Rectangle(`row_${labelText}`);
      row.width = "420px";
      row.height = "38px";
      row.thickness = 0;
      row.paddingBottom = "4px";
      a11yContainer.addControl(row);

      const lbl = new TextBlock(`lbl_${labelText}`, labelText);
      lbl.color = "#f5eedb";
      lbl.fontSize = scaledFont(14);
      lbl.fontFamily = "Segoe UI, Arial, sans-serif";
      lbl.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      lbl.left = "10px";
      row.addControl(lbl);

      const btn = Button.CreateSimpleButton(`btn_${labelText}`, initialValue);
      btn.width = "180px";
      btn.height = "28px";
      btn.color = COLORS.BRASS;
      btn.fontSize = scaledFont(12);
      btn.fontFamily = "Segoe UI, Arial, sans-serif";
      btn.cornerRadius = 4;
      btn.thickness = 1;
      btn.background = "rgba(42, 31, 61, 0.8)";
      btn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
      btn.left = "-10px";
      row.addControl(btn);

      btn.onPointerClickObservable.add(() => {
        const nextVal = onCycle(btn);
        btn.textBlock!.text = nextVal;
      });

      btn.onPointerEnterObservable.add(() => {
        btn.background = "rgba(0, 255, 136, 0.1)";
        btn.color = COLORS.BIOLUMINESCENT_GREEN;
      });
      btn.onPointerOutObservable.add(() => {
        btn.background = "rgba(42, 31, 61, 0.8)";
        btn.color = COLORS.BRASS;
      });
    };

    // 1. Colorblind Mode
    const colorblindModes: ("none" | "deuteranopia" | "protanopia" | "tritanopia")[] = [
      "none",
      "deuteranopia",
      "protanopia",
      "tritanopia",
    ];
    const colorblindLabels: Record<string, string> = {
      none: "None (Default)",
      deuteranopia: "Red-Green (Deuteranopia)",
      protanopia: "Red Weakness (Protanopia)",
      tritanopia: "Blue-Yellow (Tritanopia)",
    };
    createOptionRow(
      "Colorblind Mode",
      colorblindLabels[a11y.state.colorblindMode],
      () => {
        const currentIdx = colorblindModes.indexOf(a11y.state.colorblindMode);
        const nextIdx = (currentIdx + 1) % colorblindModes.length;
        const nextMode = colorblindModes[nextIdx];
        a11y.update({ colorblindMode: nextMode });
        return colorblindLabels[nextMode];
      }
    );

    // 2. Font Scale
    const fontScales = [1.0, 1.25, 1.5, 2.0];
    const fontLabels: Record<number, string> = {
      1.0: "1.0x (Normal)",
      1.25: "1.25x (Large)",
      1.5: "1.5x (Huge)",
      2.0: "2.0x (Extra Large)",
    };
    createOptionRow(
      "Font Scale",
      fontLabels[a11y.state.fontScale] ?? `${a11y.state.fontScale}x`,
      () => {
        const currentIdx = fontScales.indexOf(a11y.state.fontScale);
        const nextIdx = (currentIdx + 1) % fontScales.length;
        const nextScale = fontScales[nextIdx];
        a11y.update({ fontScale: nextScale });
        return fontLabels[nextScale];
      }
    );

    // 3. Reduced Motion
    createOptionRow(
      "Reduced Motion",
      a11y.state.reducedMotion ? "ON" : "OFF",
      () => {
        const nextVal = !a11y.state.reducedMotion;
        a11y.update({ reducedMotion: nextVal });
        return nextVal ? "ON" : "OFF";
      }
    );

    // 4. High Contrast UI
    createOptionRow(
      "High Contrast UI",
      a11y.state.highContrastUI ? "ON" : "OFF",
      () => {
        const nextVal = !a11y.state.highContrastUI;
        a11y.update({ highContrastUI: nextVal });
        return nextVal ? "ON" : "OFF";
      }
    );

    // 5. Auto-Knead Assist (Making Biscuits)
    createOptionRow(
      "Auto-Knead Assist",
      a11y.state.autoKneadAssist ? "ON" : "OFF",
      () => {
        const nextVal = !a11y.state.autoKneadAssist;
        a11y.update({ autoKneadAssist: nextVal });
        return nextVal ? "ON" : "OFF";
      }
    );

    // 6. Beat Window Multiplier
    const beatMultipliers = [1.0, 1.5, 2.0, 3.0];
    const beatLabels: Record<number, string> = {
      1.0: "1.0x (Standard)",
      1.5: "1.5x (Generous)",
      2.0: "2.0x (Forgiving)",
      3.0: "3.0x (Super Easy)",
    };
    createOptionRow(
      "Rhythm Hit Window",
      beatLabels[a11y.state.beatWindowMultiplier] ?? `${a11y.state.beatWindowMultiplier}x`,
      () => {
        const currentIdx = beatMultipliers.indexOf(a11y.state.beatWindowMultiplier);
        const nextIdx = (currentIdx + 1) % beatMultipliers.length;
        const nextMult = beatMultipliers[nextIdx];
        a11y.update({ beatWindowMultiplier: nextMult });
        return beatLabels[nextMult];
      }
    );

    // 7. Dialogue Advance
    createOptionRow(
      "Dialogue Advance",
      a11y.state.dialogueAdvance === "auto" ? "Auto" : "Manual",
      () => {
        const next: "auto" | "manual" = a11y.state.dialogueAdvance === "auto" ? "manual" : "auto";
        a11y.update({ dialogueAdvance: next });
        return next === "auto" ? "Auto" : "Manual";
      }
    );

    // 8. Dialogue Dwell Multiplier
    const dwellValues = [0.75, 1.0, 1.5, 2.0];
    const dwellLabels: Record<number, string> = {
      0.75: "0.75x (Faster)",
      1.0: "1.0x (Normal)",
      1.5: "1.5x (Slower)",
      2.0: "2.0x (Much Slower)",
    };
    createOptionRow(
      "Dialogue Dwell",
      dwellLabels[a11y.state.dialogueDwellMultiplier] ?? `${a11y.state.dialogueDwellMultiplier}x`,
      () => {
        const currentIdx = dwellValues.indexOf(a11y.state.dialogueDwellMultiplier);
        const nextIdx = (currentIdx + 1) % dwellValues.length;
        const nextDwell = dwellValues[nextIdx];
        a11y.update({ dialogueDwellMultiplier: nextDwell });
        return dwellLabels[nextDwell];
      }
    );

    // Spacer
    const spacer = new Rectangle("a11ySpacer");
    spacer.height = "16px";
    spacer.thickness = 0;
    spacer.background = "transparent";
    a11yContainer.addControl(spacer);

    // Back Button
    const backBtn = Button.CreateSimpleButton("a11yBackBtn", "Back");
    backBtn.width = "140px";
    backBtn.height = "36px";
    backBtn.color = "#ffffff";
    backBtn.fontSize = 14;
    backBtn.fontFamily = "Segoe UI, Arial, sans-serif";
    backBtn.cornerRadius = 6;
    backBtn.thickness = 1;
    backBtn.background = "rgba(42, 31, 61, 0.9)";
    a11yContainer.addControl(backBtn);

    backBtn.onPointerClickObservable.add(() => {
      a11yContainer.dispose();
      if (pausePanel) {
        pausePanel.isVisible = true;
      }
      this.rebuildMainFocusable();
    });

    backBtn.onPointerEnterObservable.add(() => {
      backBtn.background = "rgba(0, 255, 136, 0.15)";
      backBtn.color = COLORS.BIOLUMINESCENT_GREEN;
    });
    backBtn.onPointerOutObservable.add(() => {
      backBtn.background = "rgba(42, 31, 61, 0.9)";
      backBtn.color = "#ffffff";
    });

    // Build focusable list for the accessibility panel (cycle buttons + back button)
    // Collect all the cycle buttons created via createOptionRow, plus the back button.
    const a11yButtons: Button[] = [];
    for (const child of a11yContainer.children) {
      // Each option row is a Rectangle containing a Button
      if (child instanceof Rectangle) {
        for (const rowChild of child.children) {
          if (rowChild instanceof Button) {
            (rowChild as any).__origBg = "rgba(42, 31, 61, 0.8)";
            (rowChild as any).__origColor = COLORS.BRASS;
            a11yButtons.push(rowChild);
          }
        }
      } else if (child instanceof Button) {
        (child as any).__origBg = "rgba(42, 31, 61, 0.9)";
        (child as any).__origColor = "#ffffff";
        a11yButtons.push(child);
      }
    }
    this.setFocusable(a11yButtons, "a11y");
  }

  /**
   * Creates a labelled horizontal slider row and adds it to a parent StackPanel.
   * Returns the Slider so the caller can read its value.
   */
  private createSliderRow(
    parent: StackPanel,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
  ): Slider {
    const row = new Rectangle(`audioRow_${label}`);
    row.width = "420px";
    row.height = "44px";
    row.thickness = 0;
    row.paddingBottom = "4px";
    parent.addControl(row);

    const lbl = new TextBlock(`audioLbl_${label}`, label);
    lbl.color = "#f5eedb";
    lbl.fontSize = 14;
    lbl.fontFamily = "Segoe UI, Arial, sans-serif";
    lbl.width = "120px";
    lbl.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    lbl.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    lbl.left = "10px";
    row.addControl(lbl);

    const slider = new Slider(`audioSlider_${label}`);
    slider.minimum = 0;
    slider.maximum = 1;
    slider.value = initialValue;
    slider.width = "240px";
    slider.height = "20px";
    slider.color = COLORS.BIOLUMINESCENT_GREEN;
    slider.background = "rgba(42, 31, 61, 0.8)";
    slider.borderColor = COLORS.BRASS;
    slider.thumbWidth = 16;
    slider.isThumbCircle = true;
    slider.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    slider.left = "-10px";
    row.addControl(slider);

    const valLabel = new TextBlock(`audioVal_${label}`, `${Math.round(initialValue * 100)}%`);
    valLabel.color = COLORS.BRASS;
    valLabel.fontSize = 12;
    valLabel.fontFamily = "Segoe UI, Arial, sans-serif";
    valLabel.width = "38px";
    valLabel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    valLabel.left = "-260px";
    row.addControl(valLabel);

    slider.onValueChangedObservable.add((value) => {
      valLabel.text = `${Math.round(value * 100)}%`;
      onChange(value);
    });

    return slider;
  }

  private showAudioOverlay(): void {
    if (!this.overlay) return;

    const pausePanel = this.overlay.getChildByName("pausePanel") || this.overlay.children[0];
    if (pausePanel) {
      pausePanel.isVisible = false;
    }
    // Clear focus highlight before switching panels
    this.clearFocusHighlight();

    const save = new SaveSystem();
    const volumes = save.loadVolumes();

    const audioContainer = new StackPanel("audioPanel");
    audioContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    audioContainer.width = "480px";
    this.overlay.addControl(audioContainer);

    // Title
    const title = new TextBlock("audioTitle");
    title.text = "Audio Settings";
    title.color = COLORS.BIOLUMINESCENT_GREEN;
    title.fontSize = 28;
    title.fontFamily = "Georgia, 'Times New Roman', serif";
    title.fontWeight = "bold";
    title.height = "44px";
    audioContainer.addControl(title);

    const subtitle = new TextBlock("audioSubtitle");
    subtitle.text = "Adjust volume levels for the game.";
    subtitle.color = "#c5b089";
    subtitle.fontSize = 12;
    subtitle.fontFamily = "Segoe UI, Arial, sans-serif";
    subtitle.height = "20px";
    audioContainer.addControl(subtitle);

    const divider = new Rectangle("audioDivider");
    divider.width = "360px";
    divider.height = "2px";
    divider.thickness = 0;
    divider.background = COLORS.BRASS;
    divider.alpha = 0.4;
    divider.paddingBottom = "8px";
    audioContainer.addControl(divider);

    const saveFn = (v: VolumePrefs) => save.saveVolumes(v);

    this.createSliderRow(audioContainer, "Master", volumes.master,
      buildAudioOnChange("master", volumes, saveFn));
    this.createSliderRow(audioContainer, "SFX", volumes.sfx,
      buildAudioOnChange("sfx", volumes, saveFn));
    this.createSliderRow(audioContainer, "Music", volumes.music,
      buildAudioOnChange("music", volumes, saveFn));

    // Spacer
    const spacer = new Rectangle("audioSpacer");
    spacer.height = "16px";
    spacer.thickness = 0;
    spacer.background = "transparent";
    audioContainer.addControl(spacer);

    // Back button
    const backBtn = Button.CreateSimpleButton("audioBackBtn", "Back");
    backBtn.width = "140px";
    backBtn.height = "36px";
    backBtn.color = "#ffffff";
    backBtn.fontSize = 14;
    backBtn.fontFamily = "Segoe UI, Arial, sans-serif";
    backBtn.cornerRadius = 6;
    backBtn.thickness = 1;
    backBtn.background = "rgba(42, 31, 61, 0.9)";
    audioContainer.addControl(backBtn);

    backBtn.onPointerClickObservable.add(() => {
      audioContainer.dispose();
      if (pausePanel) {
        pausePanel.isVisible = true;
      }
      this.rebuildMainFocusable();
    });
    backBtn.onPointerEnterObservable.add(() => {
      backBtn.background = "rgba(0, 255, 136, 0.15)";
      backBtn.color = COLORS.BIOLUMINESCENT_GREEN;
    });
    backBtn.onPointerOutObservable.add(() => {
      backBtn.background = "rgba(42, 31, 61, 0.9)";
      backBtn.color = "#ffffff";
    });

    // Build focusable list for the audio panel: sliders + back button
    const audioFocusable: FocusableControl[] = [];
    for (const child of audioContainer.children) {
      if (child instanceof Rectangle) {
        for (const rowChild of child.children) {
          if (rowChild instanceof Slider) {
            audioFocusable.push(rowChild);
          }
        }
      } else if (child instanceof Button) {
        (child as any).__origBg = "rgba(42, 31, 61, 0.9)";
        (child as any).__origColor = "#ffffff";
        audioFocusable.push(child);
      }
    }
    this.setFocusable(audioFocusable, "audio");
  }

  /** Clean up all resources. Call when leaving the gameplay scene. */
  dispose(): void {
    if (this.keyHandler) {
      window.removeEventListener("keydown", this.keyHandler);
      this.keyHandler = null;
    }
    if (this.gamepadPauseObserver) {
      this.scene.onBeforeRenderObservable.remove(this.gamepadPauseObserver);
      this.gamepadPauseObserver = null;
    }
    this.gamepadPauseButtonDown = false;
    this.unsubCutsceneStarted?.();
    this.unsubCutsceneStarted = null;
    this.unsubCutsceneEnded?.();
    this.unsubCutsceneEnded = null;
    this.unsubA11y?.();
    this.unsubA11y = null;
    this.destroyOverlay();
    this.isPaused = false;
  }
}
